import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Copy, Check, Info, Loader2 } from 'lucide-react';
import {
  useEmailTemplates,
  useEmailTemplate,
} from '@/hooks/react-query/useTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import {
  EmailTemplateCategory,
  AVAILABLE_VARIABLES,
} from '@/components/admin/templates/constants';
import { renderEmailBodyHtml } from '@/utils/email-body';
import { InsertLinkButton } from '@/components/admin/templates/email/InsertLinkButton';

export default function EmailTemplateFormPage() {
  const { templateId } = useParams<{ templateId?: string }>();

  if (!templateId) {
    return <EmailTemplateForm key="new" />;
  }

  return <EmailTemplateFormPageEdit templateId={templateId} />;
}

function EmailTemplateFormPageEdit({ templateId }: { templateId: string }) {
  const { data: template, isLoading } = useEmailTemplate(templateId);

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-64" />
          <div className="h-4 bg-muted rounded w-96" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <EmailTemplateForm
      key={templateId}
      initialData={
        template
          ? {
              name: template.name,
              description: template.description || '',
              category: template.template_type as EmailTemplateCategory | '',
              subject_template: template.subject_template,
              text_template: template.text_template || '',
            }
          : undefined
      }
      templateId={templateId}
    />
  );
}

interface EmailTemplateFormProps {
  initialData?: {
    name: string;
    description: string;
    category: EmailTemplateCategory | '';
    subject_template: string;
    text_template: string;
  };
  templateId?: string;
}

function EmailTemplateForm({
  initialData,
  templateId,
}: EmailTemplateFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!templateId;

  const {
    createEmailTemplate,
    updateEmailTemplate,
    isCreatingEmail,
    isUpdatingEmail,
  } = useEmailTemplates();

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: (initialData?.category || '') as EmailTemplateCategory | '',
    subject_template: initialData?.subject_template || '',
    text_template: initialData?.text_template || '',
  });
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  const handleCopyVariable = (variable: string) => {
    navigator.clipboard.writeText(`{{ ${variable} }}`);
    setCopiedVariable(variable);
    setTimeout(() => setCopiedVariable(null), 1500);
  };

  const handleDragStart = (e: React.DragEvent, variable: string) => {
    e.dataTransfer.setData('text/plain', `{{ ${variable} }}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDropOnBody = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const text = e.dataTransfer.getData('text/plain');
    if (!text) return;

    const textarea = bodyRef.current;
    if (!textarea) return;

    const pos = textarea.selectionStart ?? formData.text_template.length;
    const before = formData.text_template.substring(0, pos);
    const after = formData.text_template.substring(pos);
    setFormData((prev) => ({ ...prev, text_template: before + text + after }));

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = pos + text.length;
    });
  };

  const handleDropOnSubject = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.dataTransfer.getData('text/plain');
    if (!text) return;

    const input = subjectRef.current;
    if (!input) return;

    const pos = input.selectionStart ?? formData.subject_template.length;
    const before = formData.subject_template.substring(0, pos);
    const after = formData.subject_template.substring(pos);
    setFormData((prev) => ({
      ...prev,
      subject_template: before + text + after,
    }));

    requestAnimationFrame(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = pos + text.length;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleSave = async () => {
    if (
      !formData.name ||
      !formData.category ||
      !formData.subject_template ||
      !formData.text_template
    ) {
      toast({
        title: 'Validation Error',
        description:
          'Please fill in all required fields (Name, Category, Subject, Body)',
        variant: 'destructive',
      });
      return;
    }
    try {
      const htmlContent = renderEmailBodyHtml(formData.text_template);
      if (isEditing && templateId) {
        await updateEmailTemplate({
          id: templateId,
          data: {
            name: formData.name,
            description: formData.description || undefined,
            subject_template: formData.subject_template,
            html_template: htmlContent,
            text_template: formData.text_template,
          },
        });
        toast({
          title: 'Success',
          description: 'Email template updated successfully',
        });
      } else {
        await createEmailTemplate({
          name: formData.name,
          description: formData.description || undefined,
          template_type: formData.category,
          subject_template: formData.subject_template,
          html_template: htmlContent,
          text_template: formData.text_template,
        });
        toast({
          title: 'Success',
          description: 'Email template created successfully',
        });
      }
      navigate('/app/admin/templates');
    } catch {
      toast({
        title: 'Error',
        description: `Failed to ${isEditing ? 'update' : 'create'} template`,
        variant: 'destructive',
      });
    }
  };

  const isSaving = isCreatingEmail || isUpdatingEmail;

  return (
    <div className="space-y-6">
      {/* Description */}
      <p className="text-muted-foreground">
        Write an email template with merge fields (e.g., {`{{ investor.name }}`}
        ). This template will be used for notification emails sent from
        Distributions and Capital Calls.
      </p>

      {/* Form Fields */}
      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email-name">Template Name *</Label>
              <Input
                id="email-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Distribution Notification Email"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email-category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    category: value as EmailTemplateCategory,
                  })
                }
                disabled={isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="distribution_notification">
                    Distribution Notification
                  </SelectItem>
                  <SelectItem value="drawdown_notification">
                    Drawdown Notification
                  </SelectItem>
                  <SelectItem value="report_notification">
                    Report Notification
                  </SelectItem>
                  <SelectItem value="general">General Notification</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="email-description">Description</Label>
            <Input
              id="email-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Brief description of when this email is sent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Available Variables */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Available Variables</span>
            <span className="text-xs text-muted-foreground">
              click to copy · drag into template
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {AVAILABLE_VARIABLES.map((v) => (
              <button
                key={v}
                type="button"
                draggable
                onClick={() => handleCopyVariable(v)}
                onDragStart={(e) => handleDragStart(e, v)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-background border hover:bg-accent transition-colors cursor-grab active:cursor-grabbing"
              >
                {copiedVariable === v ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground" />
                )}
                {`{{ ${v} }}`}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Subject Template */}
      <Card>
        <CardHeader>
          <CardTitle>Subject Line *</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            ref={subjectRef}
            id="email-subject"
            value={formData.subject_template}
            onChange={(e) =>
              setFormData({ ...formData, subject_template: e.target.value })
            }
            onDrop={handleDropOnSubject}
            onDragOver={handleDragOver}
            placeholder="e.g., Distribution Notice - {{ fund.name }} - {{ distribution.distribution_date }}"
            className="font-medium"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Tip: You can drag variables into the subject line too.
          </p>
        </CardContent>
      </Card>

      {/* Email Body */}
      <Card>
        <CardHeader>
          <CardTitle>
            Email Body *
            <span className="text-xs font-normal text-muted-foreground ml-2">
              (Plain Text with Variables)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-2">
            <InsertLinkButton
              value={formData.text_template}
              onChange={(nextValue) =>
                setFormData({ ...formData, text_template: nextValue })
              }
              textareaRef={bodyRef}
            />
          </div>
          <Textarea
            ref={bodyRef}
            id="email-body"
            value={formData.text_template}
            onChange={(e) =>
              setFormData({ ...formData, text_template: e.target.value })
            }
            onDragOver={handleDragOver}
            onDrop={handleDropOnBody}
            placeholder={`Dear {{ investor.name }},\n\nWe are pleased to inform you of a distribution from {{ fund.name }}.\n\nDistribution Amount: {{ allocation.amount }}\nDistribution Date: {{ distribution.distribution_date }}\n\nPlease find the detailed notice attached.\n\nBest regards,\n{{ organization.display_name }}`}
            rows={20}
            className="font-mono text-sm leading-relaxed"
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/app/admin/templates')}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isEditing ? 'Update' : 'Create'} Template
        </Button>
      </div>
    </div>
  );
}
